import * as React from "react";
import {
  Link,
  Box,
  Typography,
  Snackbar,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

import { FormProvider, useForm } from "react-hook-form";
import { useState } from "react";
import { resendSignUpCode, signUp } from "aws-amplify/auth";

import {
  NewUser,
  useCreateUserMutation,
} from "components/Slices/usersApiSlice";
import { useNavigate } from "react-router-dom";
import { RegisterForm, step1Schema } from "lib/schema";
import { GeneralSteps } from "./RegistrationSteps/GeneralSteps";

// Best-effort default from the browser locale; user can still change it.
const detectDefaultCountry = (): "US" | "CA" => {
  try {
    const region = new Intl.Locale(navigator.language).region;
    return region === "CA" ? "CA" : "US";
  } catch {
    return "US";
  }
};

function Register() {
  const { t } = useTranslation();
  const methods = useForm<RegisterForm>({
    mode: "all",
    defaultValues: {
      country: detectDefaultCountry(),
    },
  });

  const navigate = useNavigate();

  const [createUser] = useCreateUserMutation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const registerSubmit = async (data: RegisterForm) => {
    setLoading(true);
    try {
      const { nextStep, userId } = await signUp({
        username: data.email,
        password: data.password,
        options: {
          userAttributes: {
            email: data.email,
            given_name: data.firstName,
            family_name: data.lastName,
          },
          autoSignIn: true,
        },
      });

      //create my own users so I can create Stripe info
      const user = {
        cognitoId: userId,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        country: data.country,
      };

      // Best-effort, never fatal. The Cognito account already exists at this
      // point, so stopping here used to strand the user in a split-brain state
      // ("user exists" on every retry). If the backend call fails, stash the
      // profile so the confirm page can retry it; even if that retry also
      // fails, authMiddleware creates the row from the verified token on the
      // first authenticated request.
      const createdInternalUser = await createInternalUser(user);
      if (!createdInternalUser) {
        localStorage.setItem("pendingSignupProfile", JSON.stringify(user));
      }

      if (nextStep?.signUpStep === "CONFIRM_SIGN_UP") {
        localStorage.setItem("pendingEmail", data.email);
        navigate(`/confirm`, {
          state: {
            email: data.email,
            tempPassword: data.password,
          },
        });
      }
    } catch (error) {
      console.error("Registration error:", error);
      if ((error as { name?: string })?.name === "UsernameExistsException") {
        await resumeExistingSignup(data);
      } else if (error instanceof Error) {
        setMessage(error.message);
      } else {
        setMessage(t("auth.register.genericError"));
      }
    } finally {
      setLoading(false);
    }
  };

  // "User exists" is usually a half-finished signup: a previous attempt created
  // the Cognito account but the user never confirmed (e.g. the backend call
  // errored and the old flow dead-ended them). Resending the code only succeeds
  // for UNCONFIRMED accounts, so use it to tell the two cases apart and un-burn
  // the email instead of showing a raw error.
  const resumeExistingSignup = async (data: RegisterForm) => {
    try {
      await resendSignUpCode({ username: data.email });
      localStorage.setItem("pendingEmail", data.email);
      localStorage.setItem(
        "pendingSignupProfile",
        JSON.stringify({
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          country: data.country,
        }),
      );
      navigate(`/confirm`, {
        state: {
          email: data.email,
          // Only valid if they retyped their original password; the confirm
          // page falls back to /login when auto-sign-in fails.
          tempPassword: data.password,
        },
      });
    } catch {
      // Already confirmed (or resend refused) — this is a real existing account.
      setMessage(
        "An account with this email already exists. Try signing in instead.",
      );
    }
  };

  const createInternalUser = async (user: NewUser) => {
    try {
      await createUser(user).unwrap();
      return true;
    } catch (error) {
      console.error("Error creating internal user:", error);
      return false;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 2,
        px: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          p: { xs: 2.5, sm: 3 },
          width: "100%",
          maxWidth: 440,
        }}
      >
        {/** Header */}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: "text.primary",
              fontSize: { xs: "1.75rem", sm: "2rem" },
            }}
          >
            {t("auth.register.title")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "1rem",
              lineHeight: 1.5,
            }}
          >
            {t("auth.register.subtitle")}
          </Typography>
        </Box>
        <FormProvider {...methods}>
          <Box
            display="flex-col"
            component="form"
            onSubmit={methods.handleSubmit(registerSubmit)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              maxWidth: "100%",
            }}
          >
            <GeneralSteps isLoading={loading} />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !methods.formState.isValid}
              endIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              sx={{
                mt: 2,
                width: "80%",
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                backgroundColor: "primary.main",
              }}
            >
              {loading
                ? t("auth.register.creatingAccount")
                : t("auth.register.createAccount")}
            </Button>
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.95rem" }}
              >
                {t("auth.register.alreadyHaveAccount")}
                {"    "}
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate("/login")}
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    fontWeight: 600,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {t("auth.register.signInInstead")}
                </Link>
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1,
                color: "text.secondary",
                textAlign: "center",
                lineHeight: 1.5,
                fontSize: "0.875rem",
              }}
            >
              {t("auth.register.agreeToTermsPrefix")}{" "}
              <Link
                href="/terms"
                sx={{ color: "primary.main", fontWeight: 500 }}
              >
                {t("auth.register.termsOfService")}
              </Link>
              {"  "}
              {t("auth.register.and")}
              {"  "}
              <Link
                href="/privacy"
                sx={{ color: "primary.main", fontWeight: 500 }}
              >
                {t("auth.register.privacyPolicy")}
              </Link>
              .
            </Typography>
            <Snackbar
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              open={message !== null}
              autoHideDuration={5000}
              message={message}
              onClose={() => setMessage(null)}
              ContentProps={{
                sx: {
                  borderRadius: "5px",
                  color: "white",
                  bgcolor: "error.main",
                  fontWeight: "bold",
                  fontSize: "1rem",
                },
              }}
            />
          </Box>
        </FormProvider>
      </Paper>
    </Box>
  );
}
export default Register;
